import * as R from 'ramda';
import * as Dfns from 'date-fns/fp';

import { downloadJSONToCSV } from '../../utils/download-utils';
import { makeMemoizedGetter } from '../../utils/data';

import {
  CellData,
  DATE_FORMAT,
  DateRange,
  Header,
  PRETTY_DATE_FORMAT,
  RightTotal
} from './active-ott-constants';

export const getIsCurrentWeek = makeMemoizedGetter<string, boolean>({
  calculate: date => {
    const nextWeek = R.pipe(Dfns.parseISO, Dfns.addWeeks(1), Dfns.format(DATE_FORMAT))(date);
    const today = Dfns.format(DATE_FORMAT, new Date());
    return date <= today && nextWeek > today;
  }
});

export const getPrettyWeek = makeMemoizedGetter<string, string>({
  calculate: R.pipe(Dfns.parseISO, Dfns.format(PRETTY_DATE_FORMAT))
});

export const makeCPM = (spend: number, impressions: number): number =>
  impressions ? Math.round((spend / impressions) * 10000) / 10 : 0;

export const initDates = (): DateRange => {
  const currentMonday = Dfns.startOfISOWeek(new Date());
  const start = R.pipe(Dfns.subWeeks(4), Dfns.format(DATE_FORMAT))(currentMonday);
  const end = R.pipe(Dfns.addWeeks(5), Dfns.format(DATE_FORMAT))(currentMonday);
  return { start, end };
};

interface TransformAndDownloadDataToCSVArgs {
  rows: CellData[][];
  filteredHeaders: Header[];
  rightTotals: RightTotal[];
  byNetwork: boolean;
  isCpm: boolean;
  isSpend: boolean;
  fileName: string;
}

export const transformAndDownloadDataToCSV = ({
  rows,
  filteredHeaders,
  rightTotals,
  byNetwork,
  isCpm,
  isSpend,
  fileName
}: TransformAndDownloadDataToCSVArgs): void => {
  const csvRows: (string | number)[][] = [];
  const headerRow: (string | number)[] = ['Week'];
  for (const header of filteredHeaders) {
    let label: string;
    if (byNetwork) {
      label = header.cid ?? '';
    } else {
      label = `${header.shortCode} ${header.platform}`;
      if (header.description) {
        label = `${label} ${header.description}`;
      }
    }
    if (isSpend) {
      headerRow.push(`${label} Spend`);
    }
    if (isCpm) {
      headerRow.push(`${label} CPM`);
    }
  }
  if (!byNetwork) {
    headerRow.push('Expected');
  }
  headerRow.push('Total');
  csvRows.push(headerRow);

  for (let i = 0; i < rows.length; ++i) {
    const csvRow: (string | number)[] = [];
    const row = rows[i];
    const totals = rightTotals[i];
    csvRow.push(row[0]?.week ?? '');
    for (const col of row) {
      for (const [enabled, key] of [
        [isSpend, 'spend'],
        [isCpm, 'cpm']
      ] as const) {
        if (enabled) {
          csvRow.push(col[key] ?? '');
        }
      }
    }
    if (!byNetwork) {
      csvRow.push(totals.expected);
    }
    csvRow.push(totals.spend);
    csvRows.push(csvRow);
  }

  downloadJSONToCSV(csvRows, fileName);
};
